/* Tiny, safe Markdown renderer for ticket descriptions. No dependency, no
   surprises: the whole source is HTML-escaped first, and only markup WE emit
   goes back in — so v-html on the result can't smuggle script. Supported, on
   purpose nothing more: #/##/### headings, **bold**, *italic*, ~~strike~~,
   `code`, ``` fenced blocks, [links](https://…), [node links](slug), inline
   $\TeX$ symbol macros, - and 1. lists, > quotes, --- rules, blank-line
   paragraphs. */

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/* Inline TeX the memory agent likes to emit ($\rightarrow$ etc.). Only the
   symbol macros below are translated; a $…$ span with anything unrecognized —
   or with no macro at all, like "$5 and $10" — is left exactly as written. */
const TEX_GLYPHS: Record<string, string> = {
  rightarrow: '→', to: '→', Rightarrow: '⇒',
  leftarrow: '←', Leftarrow: '⇐', leftrightarrow: '↔', Leftrightarrow: '⇔',
  uparrow: '↑', downarrow: '↓',
  times: '×', cdot: '·', pm: '±', div: '÷',
  neq: '≠', ne: '≠', approx: '≈', equiv: '≡', sim: '~',
  leq: '≤', le: '≤', geq: '≥', ge: '≥', ll: '≪', gg: '≫',
  infty: '∞', ldots: '…', dots: '…', degree: '°', circ: '°', percent: '%',
}

/** "\rightarrow x" → "→ x", or null if the span holds no macro / an unknown one. */
function texToText(body: string): string | null {
  let known = body.includes('\\')
  const text = body.replace(/\\([a-zA-Z]+)/g, (whole, cmd: string) => {
    const glyph = TEX_GLYPHS[cmd]
    if (!glyph) known = false
    return glyph ?? whole
  })
  return known ? text.trim() : null
}

/** Inline marks on an ALREADY-ESCAPED line. Code spans go first so marks
 *  inside them survive literally. */
function inline(s: string, meId?: string, emotes?: Record<string, string>): string {
  const codes: string[] = []
  s = s.replace(/`([^`]+)`/g, (_, c: string) => {
    codes.push(c)
    return '\x00' + (codes.length - 1) + '\x00'
  })
  // Custom emotes: :name: → an inline image, when the name is in the viewer's
  // pack (im.ponies.user_emotes). Unknown shortcodes stay literal text — ":)"
  // and off-pack codes never turn into broken images. Same data-mxc hydration
  // dance as md-img (media downloads are authenticated).
  if (emotes) {
    s = s.replace(/:([a-zA-Z0-9_-]{2,32}):/g, (whole, name: string) => {
      const url = emotes[name]
      return url ? `<img class="md-emote" data-mxc="${url}" alt=":${name}:" title=":${name}:">` : whole
    })
  }
  // @-mentions: @[Name](@user:server) → a pill. The MXID target can't be a URL
  // or a node slug, so this never collides with the link rules below; running
  // it first (just after code spans, which shield literal tokens) means a
  // mention is consumed before anything else can chew on the brackets. Mine
  // gets an is-me accent. contenteditable=false keeps the pill atomic in the
  // rich editor, which re-renders through here too.
  s = s.replace(/@\[([^\]]+)\]\((@[^()\s]+:[^()\s]+)\)/g, (_w, name: string, mxid: string) => {
    const me = meId && mxid === meId ? ' is-me' : ''
    return `<span class="mention${me}" data-mxid="${mxid}" contenteditable="false">@${name}</span>`
  })
  // Images: ![alt](mxc://…) — homeserver media only (no third-party pixels).
  // Media download needs an auth header, so we emit a data-mxc placeholder and
  // the MdView component hydrates src with a token-fetched blob URL.
  s = s.replace(/!\[([^\]]*)\]\((mxc:\/\/[^\s)]+)\)/g, '<img class="md-img" data-mxc="$2" alt="$1">')
  // Images: ![alt](memfile:123) — files uploaded to the memory layer. Same
  // hydration dance: authenticated download, so MdView fills in the src.
  s = s.replace(/!\[([^\]]*)\]\(memfile:(\d+)\)/g, '<img class="md-img" data-memfile="$2" alt="$1">')
  // GIFs: ![alt](https://…giphy.com/…) — the one third-party image host we
  // embed (the GIF picker), so the no-third-party-pixels rule stays otherwise
  // intact. Anything else in an image link is left as text.
  s = s.replace(
    /!\[([^\]]*)\]\((https:\/\/(?:[a-z0-9-]+\.)*giphy\.com\/[^\s)]+)\)/g,
    '<img class="md-img md-gif" src="$2" alt="$1" loading="lazy">',
  )
  // Links: [text](href) — http(s) only (the source was escaped, so quotes in
  // hrefs are already &quot; and can't break out of the attribute).
  s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
  // Internal links: [text](node-slug) — the memory agent cross-references
  // graph nodes by slug. There's nothing for a plain href to point at, so
  // MdView turns clicks on these into node selection. contenteditable=false
  // keeps the link atomic in the rich editor (which re-renders through here),
  // exactly like the mention pill above; it's inert in read views.
  s = s.replace(/\[([^\]]+)\]\(([a-z0-9][a-z0-9-]{0,79})\)/g, '<a class="md-node-link" data-node="$2" contenteditable="false">$1</a>')
  // Inline math: $\rightarrow$ and friends become their Unicode glyphs.
  s = s.replace(/\$([^$\n]+)\$/g, (whole, body: string) => {
    const text = texToText(body)
    return text === null ? whole : '<span class="md-math">' + text + '</span>'
  })
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
  s = s.replace(/~~([^~]+)~~/g, '<s>$1</s>')
  return s.replace(/\x00(\d+)\x00/g, (_, i: string) => '<code>' + codes[Number(i)] + '</code>')
}

export interface RenderOpts {
  /** Task checkboxes are editable (the rich editor); default is read-only. */
  interactiveTasks?: boolean
  /** The viewing user's MXID — mentions of them render with an is-me accent. */
  meId?: string
  /** Custom emote pack (:name: → mxc url) — shortcodes render as inline images. */
  emotes?: Record<string, string>
}

export function renderMarkdown(src: string, opts: RenderOpts = {}): string {
  const meId = opts.meId
  const lines = escapeHtml(src.replace(/\r\n?/g, '\n')).split('\n')
  const out: string[] = []
  let para: string[] = []
  // A stack of open list levels (deepest last). Leading indentation in the
  // source picks the nesting depth, so "- a\n⎵⎵- b" renders as a real nested
  // <ul>. Each level's <li> is left open until we know nothing nests inside it.
  const listStack: { indent: number; type: 'ul' | 'ol' }[] = []
  let quote: string[] = []
  let fence: string[] | null = null

  const flushPara = () => {
    if (para.length) out.push('<p>' + para.map((l) => inline(l, meId, opts.emotes)).join('<br>') + '</p>')
    para = []
  }
  const flushList = () => {
    while (listStack.length) out.push('</li></' + listStack.pop()!.type + '>')
  }
  const flushQuote = () => {
    if (quote.length) out.push('<blockquote>' + quote.map((l) => inline(l, meId, opts.emotes)).join('<br>') + '</blockquote>')
    quote = []
  }
  const flushAll = () => {
    flushPara()
    flushList()
    flushQuote()
  }

  for (const line of lines) {
    if (fence) {
      if (/^```/.test(line.trim())) {
        out.push('<pre><code>' + fence.join('\n') + '</code></pre>')
        fence = null
      } else fence.push(line)
      continue
    }
    const t = line.trim()
    if (/^```/.test(t)) { flushAll(); fence = []; continue }
    if (!t) { flushAll(); continue }

    const h = /^(#{1,3})\s+(.*)$/.exec(t)
    if (h) { flushAll(); out.push(`<h${h[1].length + 3}>` + inline(h[2], meId, opts.emotes) + `</h${h[1].length + 3}>`); continue }
    if (/^(-{3,}|\*{3,})$/.test(t)) { flushAll(); out.push('<hr>'); continue }
    const q = /^&gt;\s?(.*)$/.exec(t)
    if (q) { flushPara(); flushList(); quote.push(q[1]); continue }
    const ul = /^[-*]\s+(.*)$/.exec(t)
    const ol = /^\d+[.)]\s+(.*)$/.exec(t)
    if (ul || ol) {
      flushPara()
      flushQuote()
      const type: 'ul' | 'ol' = ul ? 'ul' : 'ol'
      const indent = (/^[ \t]*/.exec(line)?.[0] ?? '').replace(/\t/g, '  ').length
      // Step back out of any deeper levels we've left, then: continue the
      // current level (same indent), nest a new one (deeper indent), or open
      // the first list. A deeper list is emitted before closing the parent's
      // <li>, so it nests inside that item.
      while (listStack.length && indent < listStack[listStack.length - 1].indent) {
        out.push('</li></' + listStack.pop()!.type + '>')
      }
      const top = listStack[listStack.length - 1]
      if (top && indent <= top.indent) {
        out.push('</li>')
        if (type !== top.type) {
          out.push('</' + top.type + '>')
          listStack.pop()
          out.push('<' + type + '>')
          listStack.push({ indent, type })
        }
      } else {
        out.push('<' + type + '>')
        listStack.push({ indent, type })
      }
      const content = (ul || ol)![1]
      // Task lines: "- [ ] thing" / "- [x] done" become real checkboxes.
      const task = ul ? /^\[( |x|X)\]\s?(.*)$/.exec(content) : null
      if (task) {
        const checked = task[1] !== ' '
        const attrs = (checked ? ' checked' : '') + (opts.interactiveTasks ? ' contenteditable="false"' : ' disabled')
        out.push(`<li class="md-task"><input type="checkbox" data-task${attrs}> ` + inline(task[2], meId, opts.emotes))
      } else {
        out.push('<li>' + inline(content, meId, opts.emotes))
      }
      continue
    }
    flushList()
    flushQuote()
    para.push(t)
  }
  if (fence) out.push('<pre><code>' + fence.join('\n') + '</code></pre>')
  flushAll()
  return out.join('')
}

/** Count task lines ("- [ ]" / "- [x]") in Markdown source. */
export function taskStats(src: string): { done: number; total: number } {
  let done = 0
  let total = 0
  for (const line of src.split('\n')) {
    const m = /^\s*[-*]\s+\[( |x|X)\]/.exec(line)
    if (!m) continue
    total++
    if (m[1] !== ' ') done++
  }
  return { done, total }
}

/** Flip the nth task line's checkbox in Markdown source (n counts task lines
 *  top to bottom — the same order the renderer emits them). */
export function toggleTaskAt(src: string, index: number, checked: boolean): string {
  let n = -1
  return src
    .split('\n')
    .map((line) => {
      const m = /^(\s*[-*]\s+)\[( |x|X)\](.*)$/.exec(line)
      if (!m) return line
      n++
      if (n !== index) return line
      return m[1] + (checked ? '[x]' : '[ ]') + m[3]
    })
    .join('\n')
}

/** Markdown source → plain one-liner, for card snippets. */
export function stripMarkdown(src: string): string {
  return src
    .replace(/```[\s\S]*?(```|$)/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\$([^$\n]+)\$/g, (whole, body) => texToText(body) ?? whole)
    .replace(/^#{1,3}\s+/gm, '')
    .replace(/^>\s?/gm, '')
    .replace(/^[-*]\s+\[( |x|X)\]\s?/gm, '')
    .replace(/^[-*]\s+/gm, '')
    .replace(/^\d+[.)]\s+/gm, '')
    .replace(/(\*\*|\*|~~)/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}
